import { ButtonHTMLAttributes, useState } from "react";
import { Note, NoteReply, SaveNoteReplayModel } from "../../types/note/Note";
import dateTimeUtils from "../../utils/dateTime.utils";
import { isRequesting } from "../../utils/request-state.utils";
import { stringUtils } from "../../utils/string.utils";
import { user } from "../../user/user";
import { Confirm } from "../alerts/Confirm";
import { EditNote, NoteContent } from "./EditNote";
import { RequestState } from "../../constants/request-state";
import { NoteReplyList } from "./reply/NoteReplyList";
import { NoteReplyView } from "./reply/NoteReplyView";
import { EditNoteReply } from "./reply/EditNoteReply";
import IconSVG from "../../styles/svg-icons";
import { NotesRequestState } from "./state/notes-request-state";
import { compareDatesAsc } from "../../utils/compare.utils";
import { OnHoverTooltip } from "../common/OnHoverTooltip";

export interface Props {
    note: Note
    noteSaveRequestState: RequestState
    noteReplySaveRequestState: NotesRequestState
    noteDeleteRequestState: RequestState
    noteReplyDeleteRequestState: NotesRequestState
    renderCustomHeaderContent?: (note: Note) => React.ReactNode
    onNoteSave: (note: NoteContent, original: Note) => Promise<boolean>
    onNoteReplySave: (reply: SaveNoteReplayModel) => Promise<boolean>
    onNoteDelete: (note: Note) => void
    onNoteReplyDelete: (reply: NoteReply) => void
}

export function NoteView({
    note,
    noteSaveRequestState,
    noteReplySaveRequestState,
    noteDeleteRequestState,
    noteReplyDeleteRequestState,

    renderCustomHeaderContent,
    onNoteSave,
    onNoteReplySave,
    onNoteDelete,
    onNoteReplyDelete }: Props) {
    const [isReplyEdit, setReplyEdit] = useState(false);
    const [isEdit, setEdit] = useState(false);

    const isMy = stringUtils.equalsIgnoringCase(note.user.email, user.current()?.email ?? '');
    const isRequestingChanges = isRequesting(noteSaveRequestState, noteDeleteRequestState);

    const handleNoteSave = async (changes: NoteContent) => {
        const isSaved = await onNoteSave(changes, note);

        if (isSaved) {
            setEdit(false);
        }
    }

    const handleNoteDelete = () => {
        onNoteDelete(note);
    }

    const handleReplySave = async (text: string) => {
        const isSaved = await onNoteReplySave({ noteId: note.id, text });
        if (isSaved) {
            setReplyEdit(false);
        }
    }

    return (
        <div className="notes-item">
            <div className="notes-head">
                <span className="notes-user-name">
                    <span className="notes-user-info">
                        <OnHoverTooltip overlay={`${note.user.firstName} ${note.user.lastName}`}>
                            {note.user.firstName} {note.user.lastName}
                        </OnHoverTooltip>
                    </span>
                    {note.isPrivate && <span className="notes-private-flag">(private)</span>}
                </span>
                <span className="notes-publish-date">
                    {dateTimeUtils.utcToLocalString(note.submittedDate)}
                </span>
                {renderCustomHeaderContent?.(note)}
                <div className="notes-action-buttons">
                    <ReplyButton disabled={isReplyEdit} onClick={() => setReplyEdit(true)} />
                    {isMy && <>
                        <EditNoteButton disabled={isEdit || isRequestingChanges} onClick={() => setEdit(true)} />
                        <DeleteNoteButton disabled={isRequestingChanges} onClick={handleNoteDelete} />
                    </>}
                </div>
            </div>

            {isEdit ? (
                <EditNote
                    isPrivate={note.isPrivate}
                    text={note.text}
                    saveButtonText="save"
                    requestState={noteSaveRequestState}
                    onSave={handleNoteSave}
                    onCancel={() => setEdit(false)}
                />) : note.text}

            {!!note.replies.length &&
                <NoteReplyList>
                    {[...note.replies].sort((a, b) => compareDatesAsc(a.submittedDate, b.submittedDate)).map(r =>
                        <NoteReplyView
                            key={r.id}
                            reply={r}
                            note={note}
                            saveRequestState={noteReplySaveRequestState[r.id]}
                            deleteRequestState={noteReplyDeleteRequestState[r.id]}
                            onSave={onNoteReplySave}
                            onDelete={onNoteReplyDelete}
                        />)}
                </NoteReplyList>
            }
            {isReplyEdit &&
                <EditNoteReply
                    note={note}
                    requestState={noteReplySaveRequestState[0]}
                    onCancel={() => setReplyEdit(false)}
                    onSave={handleReplySave}
                />}
        </div>
    );
}

function ReplyButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button type="button" aria-label="reply" className="btn-link" {...props}>
            <IconSVG
                name="reply"
                width={16}
                height={16}
            />
        </button>
    );
}

function EditNoteButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button type="button" className="btn-link" aria-label="edit note" {...props}>
            <IconSVG
                name="edit-pencil"
                width={16}
                height={16}
            />
        </button>
    );
}

function DeleteNoteButton({ onClick, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
    const [isConfirm, setConfirm] = useState(false)

    return (
        <>
            <button type="button" aria-label="delete note" className="btn-link btn-danger" {...props} onClick={() => setConfirm(true)}>
                <IconSVG
                    name="basket"
                    width={16}
                    height={16}
                />
            </button>
            {
                isConfirm &&
                <Confirm
                    renderInBody
                    text="Are you sure you want to delete the note?"
                    onConfirm={(e: React.MouseEvent<HTMLButtonElement>) => {
                        onClick?.(e);
                        setConfirm(false);
                    }}
                    onCancel={() => setConfirm(false)}
                />
            }
        </>
    );
}